import React, { useEffect, useState } from "react";
import { fetchMonthlyThroughput } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme, CircularProgress, Box } from "@mui/material";
import PropTypes from "prop-types";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const MonthlyThroughputChart = ({ date, workcellId, onClick, activeIndex, onHover, loading, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatInt = (value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
  };

  useEffect(() => {
    if (!date || !workcellId) {
      setData([]);
      return;
    }
    setInternalLoading(true);
    const callParent = (typeof onLoadingChange === 'function');
    callParent && onLoadingChange(true);
    fetchMonthlyThroughput(date, workcellId)
      .then((apiData) => {
        if (apiData.length === 0) {
          setData([]);
          setInternalLoading(false);
          return;
        }

        const days = getMonthDays(date);
        const fullMonthData = days.map((day) => ({
          day,
          work_done: 0,
        }));

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullMonthData.findIndex((d) => d.day === entry.day);
          if (index !== -1) {
            fullMonthData[index].work_done = entry.work_done;
          }
        });

        setData(fullMonthData);
        setInternalLoading(false);
        callParent && onLoadingChange(false);
      })
      .catch((error) => {
        console.error(error);
        setData([]);
        setInternalLoading(false);
        callParent && onLoadingChange(false);
      })
      .finally(() => {
        setInternalLoading?.(false);
        callParent && onLoadingChange(false);
      });
  }, [date, workcellId]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ background: '#fff', border: '1px solid #ccc', padding: 8 }}>
          <div><b>{t("common.date")}:</b> {label}</div>
          <div style={{ color: theme.palette.primary.main }}>
            {t("workcell.monthlyOverview.throughput.tooltip.workDone", { count: formatInt(payload[0].value) })}
          </div>
        </div>
      );
    }
    return null;
  };

  const isLoading = typeof loading === 'boolean' ? loading : internalLoading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }
  if (data.length === 0)
    return <div>{t("common.noData")}</div>;
  else {
    // Custom tick for month name (only show for first day of month)
    const MonthTick = (props) => {
      const { x, y, payload, index } = props;
      const date = parseLocalDateTime(payload.value);
      const isFirst = index === 0;
      const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
      const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
      return isMonthStart ? (
        <g>
          <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
            {date.toLocaleString(locale, { month: 'long' })}
          </text>
        </g>
      ) : null;
    };

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          syncId="workcellCharts"
        >
          {/* Main X axis: days */}
          <XAxis
            dataKey="day"
            tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
            height={30}
            axisLine={true}
            tickLine={true}
          />
          {/* Secondary X axis: month name */}
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            height={20}
            xAxisId="month"
            tick={MonthTick}
            interval={0}
            allowDataOverflow={true}
            orientation="bottom"
          />
          <YAxis
            label={{
              value: t("workcell.monthlyOverview.throughput.axisLabel"),
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle" }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="work_done"
            fill={theme.palette.primary.main}
            onClick={(data, index) => {
              if (onClick) onClick(data.day)
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
};

MonthlyThroughputChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
  loading: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

export default MonthlyThroughputChart;
